'use client';

import { useParams } from 'next/navigation';
import { CSSProperties, FC } from 'react';
import rewritesMap from 'root/.build/rewrites-map.json';
import { gtmPushDownload } from 'modules/gtm/modules/sections/page/page';
import { NextLink } from 'modules/i18n/navigation';
import { usePathname } from 'modules/next/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';

export type ILinkProps = Parameters<typeof NextLink>[0] & {
  style?: CSSProperties;
};
// #some or scheme:(mailto/tel/https/ftp)
export const checkExternalLinks = (url: string) =>
  /^(#|\w{1,}:)/.test(url.trim());

export const formatUrl = (url: string, locale: string) => {
  if (checkExternalLinks(url)) {
    return url.trim().replace(/([^:])\/\/+/g, '$1/');
  } else {
    const _url = new URL(url, 'http://test.com');

    const pathname = _url.pathname
      .replace(/\/{1,}/g, '/')
      .replace(new RegExp(`^\/{0,1}${locale}`), '');

    return `${pathname}${_url.search}${_url.hash}`;
  }
};

export const Link: FC<ILinkProps> = ({ href, children, ...props }) => {
  const pathname = usePathname();
  const { locale } = useParams();
  const { togglePageLoading } = useSiteConfig();

  if (typeof href === 'object') {
    href.pathname = href.pathname
      ? rewritesMap[href.pathname] || href.pathname
      : href.pathname;

    href.pathname = formatUrl(href.pathname || '', locale as string);
  } else if (typeof href === 'string') {
    href = formatUrl(rewritesMap[href] || href, locale as string);
  } else {
    href = rewritesMap[href] || href;
  }

  const handleDownloadLink = (href?: string | null) => {
    if (href && (href.includes('download') || href.includes('pdf'))) {
      gtmPushDownload({
        fileName: href.split('/').pop() || '',
        linkUrl: href,
      });
    }
  };

  return (
    <NextLink
      href={href}
      prefetch={false}
      {...props}
      onClick={(e) => {
        const _href = typeof href === 'object' ? href.pathname : href;
        handleDownloadLink(_href);

        _href !== pathname &&
          props.target !== '_blank' &&
          !checkExternalLinks(_href || '') &&
          togglePageLoading?.(true);

        props?.onClick?.(e);
      }}
    >
      {children}
    </NextLink>
  );
};
